import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Mail,
  User,
  Calendar,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

interface ManagerApprovalStepProps {
  onComplete: () => void;
  onSkip: () => void;
  onPrevious: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  isRequired: boolean;
  estimatedTime: string;
}

interface ApprovalRequest {
  id: string;
  managerName: string;
  managerEmail: string;
  requestedAt: string;
  status: 'pending' | 'approved' | 'rejected' | 'needs_changes';
  notes?: string;
  responseAt?: string;
}

export const ManagerApprovalStep: React.FC<ManagerApprovalStepProps> = ({
  onComplete,
  onSkip,
  onPrevious,
  canGoBack,
  isRequired,
  estimatedTime
}) => {
  const [approvalRequest, setApprovalRequest] = useState<ApprovalRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState('');

  useEffect(() => {
    loadApprovalStatus();
  }, []);

  const loadApprovalStatus = async () => {
    try {
      setLoading(true);
      // Mock approval request - in real implementation, this would come from the backend
      const mockRequest: ApprovalRequest = {
        id: 'req_001',
        managerName: 'Sarah Johnson',
        managerEmail: 'sarah.johnson@company.com',
        requestedAt: new Date().toISOString(),
        status: 'pending'
      };

      setApprovalRequest(mockRequest);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleRequestApproval = async () => {
    try {
      setSubmitting(true);

      // In a real implementation, this would send a request to the backend
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call

      toast.success('Approval request sent to your manager');

      // Update the request status
      setApprovalRequest(prev =>
        prev
          ? {
              ...prev,
              status: 'pending',
              requestedAt: new Date().toISOString()
            }
          : null
      );
    } catch (error) {
      toast.error('Failed to send approval request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendRequest = async () => {
    try {
      setSubmitting(true);

      // In a real implementation, this would resend the request
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call

      toast.success('Approval request resent to your manager');
    } catch (error) {
      toast.error('Failed to resend approval request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className='h-5 w-5 text-green-600' />;
      case 'rejected':
        return <AlertCircle className='h-5 w-5 text-red-600' />;
      case 'needs_changes':
        return <AlertCircle className='h-5 w-5 text-orange-600' />;
      default:
        return <Clock className='h-5 w-5 text-blue-600' />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant='default' className='bg-green-600'>
            Approved
          </Badge>
        );
      case 'rejected':
        return <Badge variant='destructive'>Rejected</Badge>;
      case 'needs_changes':
        return (
          <Badge variant='outline' className='border-orange-300 text-orange-600'>
            Needs Changes
          </Badge>
        );
      default:
        return <Badge variant='secondary'>Pending</Badge>;
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Your manager has approved your account. You can now proceed to activation.';
      case 'rejected':
        return 'Your manager has rejected your account request. Please contact them for more information.';
      case 'needs_changes':
        return 'Your manager has requested changes to your profile. Please review their feedback and update your information.';
      default:
        return 'Your approval request is pending review by your manager.';
    }
  };

  if (loading) {
    return (
      <div className='space-y-6'>
        <div className='animate-pulse'>
          <div className='h-8 bg-gray-200 rounded w-1/3 mb-4' />
          <div className='h-32 bg-gray-200 rounded' />
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='text-center space-y-2'>
        <div className='flex items-center justify-center gap-2'>
          <Users className='h-6 w-6 text-primary' />
          <h3 className='text-xl font-semibold'>Manager Approval</h3>
        </div>
        <p className='text-muted-foreground'>
          Your account requires manager approval before activation
        </p>
        <Badge variant='outline'>{estimatedTime}</Badge>
      </div>

      {/* Approval Status */}
      {approvalRequest && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              {getStatusIcon(approvalRequest.status)}
              Approval Status
            </CardTitle>
            <CardDescription>{getStatusMessage(approvalRequest.status)}</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            {/* Manager Information */}
            <div className='flex items-center gap-4 p-4 bg-muted/50 rounded-lg'>
              <div className='w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center'>
                <User className='h-6 w-6 text-primary' />
              </div>
              <div className='flex-1'>
                <h4 className='font-semibold'>{approvalRequest.managerName}</h4>
                <p className='text-sm text-muted-foreground'>{approvalRequest.managerEmail}</p>
                <p className='text-xs text-muted-foreground'>Your Manager</p>
              </div>
              {getStatusBadge(approvalRequest.status)}
            </div>

            {/* Request Details */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='flex items-center gap-2'>
                <Calendar className='h-4 w-4 text-muted-foreground' />
                <div>
                  <p className='text-sm font-medium'>Requested</p>
                  <p className='text-sm text-muted-foreground'>
                    {new Date(approvalRequest.requestedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {approvalRequest.responseAt && (
                <div className='flex items-center gap-2'>
                  <CheckCircle className='h-4 w-4 text-muted-foreground' />
                  <div>
                    <p className='text-sm font-medium'>Responded</p>
                    <p className='text-sm text-muted-foreground'>
                      {new Date(approvalRequest.responseAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Manager Notes */}
            {approvalRequest.notes && (
              <div className='p-4 bg-muted/50 rounded-lg'>
                <div className='flex items-center gap-2 mb-2'>
                  <MessageSquare className='h-4 w-4 text-muted-foreground' />
                  <span className='text-sm font-medium'>Manager Notes</span>
                </div>
                <p className='text-sm text-muted-foreground'>{approvalRequest.notes}</p>
              </div>
            )}

            {/* Additional Notes */}
            <div>
              <Label htmlFor='additional_notes'>Additional Notes (Optional)</Label>
              <Textarea
                id='additional_notes'
                value={additionalNotes}
                onChange={e => setAdditionalNotes(e.target.value)}
                placeholder='Add any additional information for your manager...'
                className='mt-2'
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className='space-y-4'>
        {approvalRequest?.status === 'pending' && (
          <div className='flex items-center gap-2 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
            <Clock className='h-5 w-5 text-blue-600' />
            <div className='flex-1'>
              <p className='text-sm font-medium text-blue-900'>Waiting for Approval</p>
              <p className='text-sm text-blue-700'>
                Your manager will review your profile and respond to your request.
              </p>
            </div>
            <Button variant='outline' size='sm' onClick={handleResendRequest} disabled={submitting}>
              {submitting ? 'Sending...' : 'Resend Request'}
            </Button>
          </div>
        )}

        {approvalRequest?.status === 'approved' && (
          <div className='flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg'>
            <CheckCircle className='h-5 w-5 text-green-600' />
            <div className='flex-1'>
              <p className='text-sm font-medium text-green-900'>Approved!</p>
              <p className='text-sm text-green-700'>
                Your manager has approved your account. You can now proceed to activation.
              </p>
            </div>
          </div>
        )}

        {approvalRequest?.status === 'rejected' && (
          <div className='flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg'>
            <AlertCircle className='h-5 w-5 text-red-600' />
            <div className='flex-1'>
              <p className='text-sm font-medium text-red-900'>Request Rejected</p>
              <p className='text-sm text-red-700'>
                Your manager has rejected your account request. Please contact them for more
                information.
              </p>
            </div>
          </div>
        )}

        {approvalRequest?.status === 'needs_changes' && (
          <div className='flex items-center gap-2 p-4 bg-orange-50 border border-orange-200 rounded-lg'>
            <AlertCircle className='h-5 w-5 text-orange-600' />
            <div className='flex-1'>
              <p className='text-sm font-medium text-orange-900'>Changes Required</p>
              <p className='text-sm text-orange-700'>
                Your manager has requested changes to your profile. Please review their feedback and
                update your information.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className='text-lg'>Need Help?</CardTitle>
          <CardDescription>
            If you have questions about the approval process, you can contact your manager directly.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex items-center gap-4'>
            <div className='flex items-center gap-2'>
              <Mail className='h-4 w-4 text-muted-foreground' />
              <span className='text-sm'>{approvalRequest?.managerEmail}</span>
            </div>
            <Button variant='outline' size='sm'>
              Send Email
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className='flex items-center justify-between pt-6 border-t'>
        <div className='flex gap-2'>
          {canGoBack && (
            <Button variant='outline' onClick={onPrevious}>
              <ArrowLeft className='h-4 w-4 mr-2' />
              Previous
            </Button>
          )}
        </div>

        <div className='flex gap-2'>
          {!isRequired && (
            <Button variant='ghost' onClick={onSkip}>
              Skip for now
            </Button>
          )}
          {approvalRequest?.status === 'approved' && (
            <Button onClick={onComplete}>
              Continue to Activation
              <ArrowRight className='h-4 w-4 ml-2' />
            </Button>
          )}
          {approvalRequest?.status === 'pending' && (
            <Button onClick={handleRequestApproval} disabled={submitting}>
              {submitting ? 'Sending...' : 'Request Approval'}
            </Button>
          )}
          {approvalRequest?.status === 'needs_changes' && (
            <Button onClick={() => (window.location.href = '/profile/edit')}>Update Profile</Button>
          )}
        </div>
      </div>
    </div>
  );
};
